import { useEffect, useState } from "react";
import { Box, TextField, Em, Text, Flex } from "@radix-ui/themes";
import styles from "./InlineNumberField.module.scss";

interface InlineNumberFieldProps {
  value?: number;
  onChange: (value: number | undefined) => void;
  placeholder?: number;
  label?: string;
  description?: string;
  min?: number;
  max?: number;
  integer?: boolean;
}

export function InlineNumberField({
  value,
  onChange,
  placeholder,
  label,
  description,
  min,
  max,
  integer = false,
}: InlineNumberFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState<number | undefined>(value);

  const handleBlur = () => {
    setIsEditing(false);
    if (localValue !== value) {
      onChange(localValue);
    }
  };

  const handleFocus = () => {
    setIsEditing(true);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(Number(event.target.value));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      setIsEditing(false);

      if (localValue !== value) {
        onChange(localValue);
      }
    }
  };

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const isEmpty = !localValue;

  return (
    <Flex gap={"2"} direction={"column"} className={styles.FieldGroupWrapper}>
      <Box>
        <Flex justify={"between"} gap={"3"}>
          {(label != null || description != null) && (
            <Flex direction={"column"} gap={"1"}>
              {label != null && (
                <Text size={"2"} as={"label"}>
                  {label}
                </Text>
              )}

              {description != null && (
                <Text size={"2"} color={"gray"}>
                  <Em>{description}</Em>
                </Text>
              )}
            </Flex>
          )}

          <Flex>
            <Box
              onClick={handleFocus}
              className={styles.InlineNumberFieldContainer}
            >
              {isEditing ? (
                <TextField.Root
                  size="2"
                  placeholder={placeholder?.toString()}
                  type="number"
                  value={localValue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  min={min}
                  max={max}
                  step={integer ? "1" : undefined}
                  className={styles.InlineNumberField}
                />
              ) : (
                <Text
                  size={"2"}
                  color={"gray"}
                  truncate
                  highContrast={!isEmpty}
                  className={styles.InlineNumberFieldValue}
                >
                  {!isEmpty ? localValue : <Em>{placeholder}</Em>}
                </Text>
              )}
            </Box>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
}
